import { Body2, Button, Caption1, Subtitle2 } from "@fluentui/react-components";
import { Checkmark24Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";

import { usePlatform } from "../../../features/platform-detection/use-platform.js";
import { useAudio } from "../../../hooks/use-audio.js";
import { BaseModal } from "./base-modal.jsx";
import type { SharedModalProps } from "./base-modal.jsx";

const CHECK_PERMISSIONS_DELAY_MS = 5000;

interface StepByStepImageProps {
  hasBrowserAccess: boolean;
  isTeamsWebClient: boolean;
  hasTeamsAccess: boolean;
}

const StepByStepImage = ({
  hasBrowserAccess,
  isTeamsWebClient,
  hasTeamsAccess,
}: StepByStepImageProps) => {
  if (!hasTeamsAccess && isTeamsWebClient) {
    return (
      <img
        alt="Audio permissions setup"
        className="max-h-full max-w-full object-contain"
        height={1480}
        src="/images/allow-teams-access-to-microphone.png"
        width={1524}
      />
    );
  }

  if (!hasBrowserAccess) {
    return (
      <img
        alt="Allow browser access"
        className="max-h-full max-w-full object-contain"
        height={1536}
        src="/images/allow-browser-access-to-microphone.png"
        width={2028}
      />
    );
  }

  return null;
};

interface StepCircleProps {
  completed: boolean;
  stepName: string;
}

const StepCircle = ({ completed, stepName }: StepCircleProps) => (
  <div className="flex flex-col items-center">
    <div
      className={`flex size-8 items-center justify-center rounded-full text-sm font-semibold ${
        completed
          ? "bg-green-500 text-white"
          : "border-2 border-gray-300 bg-gray-200 text-gray-700"
      }`}
    >
      {completed ? (
        <Checkmark24Regular className="size-5 text-white" />
      ) : (
        stepName
      )}
    </div>
  </div>
);

interface StepProps {
  title: string;
  description: string;
  count: number;
  completed: boolean;
  hasNextStep: boolean;
}

const Step = ({
  title,
  description,
  count,
  completed,
  hasNextStep,
}: StepProps) => (
  <div className="Step flex flex-row gap-2">
    <div className="flex flex-col items-center gap-2">
      <StepCircle completed={completed} stepName={String(count)} />

      {Boolean(hasNextStep) && <div className="mt-2 h-16 w-0.5 bg-gray-300" />}
    </div>
    <div className="flex flex-col gap-2">
      <Subtitle2>{title}</Subtitle2>
      <Caption1 className="text-gray-600">{description}</Caption1>
    </div>
  </div>
);

interface CompletedActionProps {
  onClose: (() => void) | undefined;
  show: boolean;
}

const CompletedAction = ({ onClose, show }: CompletedActionProps) => {
  if (!show) {
    return null;
  }

  return (
    <Button appearance="primary" onClick={onClose}>
      Close
    </Button>
  );
};

export const RequestAudioPermissionsModal = ({ onClose }: SharedModalProps) => {
  const { testAudioPermissions } = useAudio();
  const { isTeamsWebClient } = usePlatform();
  const [hasBrowserAccess, setHasBrowserAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);

  // We have to show Teams Access first, because after testing the feature, we found out that Teams Access is actually blocking
  // us from checking the browser access.
  // ! When we tested this in M365 and Outlook, there is no way to open App Permissions in Outlook and it does not open anything in M365.
  // ! Due to that, we don't have access to the microphone.
  // ! We should not be opening this modal in M365 and Outlook, because it will confuse the user and they cannot do anything about it.
  const steps = [
    {
      completed: hasTeamsAccess,
      description:
        "Allow Teams access to your microphone for seamless integration with Microsoft Teams.",
      show: isTeamsWebClient(),
      title: "Teams Access",
    },
    {
      completed: hasBrowserAccess,
      description:
        "We need to get access to your microphone and speakers from the browser. Please see the image for detailed steps.",
      show: true,
      title: "Browser Access",
    },
  ].filter((step) => step.show);

  const isCompleted =
    hasBrowserAccess &&
    ((isTeamsWebClient() && hasTeamsAccess) || !isTeamsWebClient());

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkPermissions = async () => {
      const result = await testAudioPermissions();

      setHasBrowserAccess(result.browserPermissions);
      setHasTeamsAccess(result.teamsPermissions);

      // If any permission is false, schedule another check in 5 seconds
      if (
        !result.browserPermissions ||
        (isTeamsWebClient() && !result.teamsPermissions)
      ) {
        timeoutId = setTimeout(checkPermissions, CHECK_PERMISSIONS_DELAY_MS);
      }
    };

    // Start the initial check immediately
    checkPermissions();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [testAudioPermissions, isTeamsWebClient]);

  return (
    <BaseModal
      actionComponent={<CompletedAction onClose={onClose} show={isCompleted} />}
      title="Audio Permissions Required"
      trap={!isCompleted}
    >
      <div className="flex flex-col gap-6">
        <Body2>
          It seems that we don't have access to your microphone. To use BeZoned,
          this permission is crucial. Please follow the steps below to grant the
          required permissions.
        </Body2>

        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-2 flex flex-col gap-4">
            {steps.map((step, index) => (
              <Step
                completed={step.completed}
                count={index + 1}
                description={step.description}
                hasNextStep={
                  index < steps.length - 1 && (steps[index + 1]?.show ?? false)
                }
                key={step.title}
                title={step.title}
              />
            ))}
          </div>

          <div className="col-span-2">
            <StepByStepImage
              hasBrowserAccess={hasBrowserAccess}
              hasTeamsAccess={hasTeamsAccess}
              isTeamsWebClient={isTeamsWebClient()}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
